<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useDefaultFieldValues } from '@/components/form-builder/field-settings/useDefaultFieldValues'

interface Props {
  field: {
    allowDecimals: boolean
    minimumValue?: number
    maximumValue?: number
    defaultValue?: number | null
    uniqueKey: () => string
  }
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (
    e: 'modify-field',
    payload: {
      propertyKey: string
      fieldValue: number | boolean | null | undefined
      key: string
    },
  ): void
}>()

const minimumValue = ref(props.field.minimumValue)
const maximumValue = ref(props.field.maximumValue)

const isInteger = computed(() => !props.field.allowDecimals)

function toggleNumberType() {
  emitChanges('allowDecimals', !props.field.allowDecimals)
}

function updateMinimum(value: string) {
  const parsedValue = isInteger.value
    ? value
      ? parseInt(value)
      : undefined
    : value
      ? parseFloat(value)
      : undefined

  emitChanges('minimumValue', parsedValue)
}

function updateMaximum(value: string) {
  const parsedValue = isInteger.value
    ? value
      ? parseInt(value)
      : undefined
    : value
      ? parseFloat(value)
      : undefined

  emitChanges('maximumValue', parsedValue)
}

const { showDefaultValue, toggleDefaultValue } = useDefaultFieldValues(props.field)

function emitChanges(propertyKey: string, fieldValue: number | boolean | null | undefined) {
  emit('modify-field', {
    propertyKey,
    fieldValue,
    key: props.field.uniqueKey(),
  })
}
</script>

<template>
  <vs-wrapper space="small">
    <v-radio-group
      :value="props.field.allowDecimals"
      label="Choose a number input: "
      mandatory
      row
      @change="toggleNumberType"
    >
      <v-radio :value="false" label="Integer"></v-radio>
      <v-radio :value="true" label="Decimal"></v-radio>
    </v-radio-group>

    <v-row>
      <v-col>
        <vs-text-input
          v-model.lazy="minimumValue"
          label="Minimum"
          type="number"
          @input="updateMinimum"
        />
      </v-col>
      <v-col>
        <vs-text-input
          v-model.lazy="maximumValue"
          label="Maximum"
          type="number"
          @input="updateMaximum"
        />
      </v-col>
    </v-row>

    <v-checkbox
      :input-value="showDefaultValue"
      label="Default Value"
      @change="toggleDefaultValue"
    />

    <div v-if="showDefaultValue">
      <vs-text-input
        v-model.number="field.defaultValue"
        label=""
        placeholder="Enter default number"
        type="number"
      />
    </div>
  </vs-wrapper>
</template>
